"use client"
import { useMemo } from 'react'
import Markdown from '@/app/common/Markdown'
import { parseAndSplitByH2 } from '@/lib/parseMarkdown'
import { type PerspectiveCategory } from '@/app/common/blocks/perspectiveTabContext'
import { ScenarioEndFooter, type ScenarioEnding } from './ScenarioEndFooter'

// The Public/Insider perspectives are standalone linear documents (no branching,
// no timeline diagram inline), so they render as a flat markdown page inside the
// normal playbook shell. They end on the same cap as the main scenario: the
// "you've reached the end" heading and the explorable timeline diagram
// (see ScenarioEndFooter).

// A flat document is normally a standalone perspective, so map the active
// perspective to the matching end-cap heading. The full PerspectiveCategory is
// covered (including mainScenario) so an unexpected value lands on a CORRECT
// heading rather than being coerced to a wrong-but-typechecking one — the trap
// that put "end of the Public's Perspective" under non-public endings.
const PERSPECTIVE_ENDING: Record<PerspectiveCategory, ScenarioEnding> = {
  mainScenario: 'mainScenario',
  public: 'public',
  insider: 'insider',
}

export const PerspectiveFlatDocument = ({
  markdown,
  selectedPerspective,
}: {
  markdown: string
  selectedPerspective: PerspectiveCategory
}) => {
  const chunks = useMemo(() => {
    if (!markdown.trim()) return []
    try {
      return parseAndSplitByH2(markdown)
    } catch {
      return []
    }
  }, [markdown])

  const ending: ScenarioEnding = PERSPECTIVE_ENDING[selectedPerspective]

  return (
    <div>
      <div className="markdown-content">
        {chunks.map((chunk, i) => (
          <Markdown key={i} nodes={chunk.nodes} docContext={chunk.docContext} />
        ))}
      </div>
      <ScenarioEndFooter ending={ending} />
    </div>
  )
}
